/**
 * Live Activity — unified API for nap-timer lock-screen presence.
 *
 * Dispatches to the native Capacitor plugin (iOS 16.2+, dynamic island + lock
 * screen live timer) when available. Falls back to a persistent Web Notification
 * with the start time embedded — no live-updating timer on web, but visible
 * lock-screen entry with tap-to-end.
 *
 * For iOS: the native plugin `BabyOSLiveActivity` must be linked into the
 * Capacitor iOS app. Swift plugin code lives at
 *   ios/App/App/Plugins/BabyOSLiveActivity/
 * and the Widget bundle already has the SleepActivityAttributes + view at
 *   ios/Widgets/BabyOSWidgets/BabyOSWidgets.swift.
 * See DAN-STEPS.md at the repo root for compile + install instructions.
 */

interface StartNapParams {
  babyName: string
  location: string
  startedAtMs: number
}

interface Capacitor {
  isNativePlatform(): boolean
  Plugins: Record<string, unknown>
}

function capacitor(): Capacitor | null {
  const g = globalThis as unknown as { Capacitor?: Capacitor }
  return g.Capacitor ?? null
}

interface LiveActivityPlugin {
  startNap(p: { babyName: string; location: string; startedAt: number }): Promise<{ activityId: string }>
  endNap(p?: { activityId?: string | null }): Promise<void>
}

function iosPlugin(): LiveActivityPlugin | null {
  const c = capacitor()
  if (!c || !c.isNativePlatform()) return null
  const p = c.Plugins['BabyOSLiveActivity'] as LiveActivityPlugin | undefined
  return p ?? null
}

const NAP_TAG = 'babyos-nap-active'
const STORAGE_KEY = 'babyos.liveactivity.napid'

export async function startNapLiveActivity(p: StartNapParams): Promise<void> {
  // iOS native path
  const ios = iosPlugin()
  if (ios) {
    try {
      const res = await ios.startNap({ babyName: p.babyName, location: p.location, startedAt: p.startedAtMs })
      localStorage.setItem(STORAGE_KEY, res.activityId)
      return
    } catch {
      // fall through to web fallback
    }
  }

  // Web fallback: persistent notification with the start time in the body
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  if (!('serviceWorker' in navigator)) return
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return
  const startedText = new Date(p.startedAtMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  await reg.showNotification(`${p.babyName} is sleeping`, {
    body: `Started at ${startedText} in ${p.location} · tap when they wake`,
    tag: NAP_TAG,
    icon: '/icon-192.png',
    badge: '/icon-192.png',
    requireInteraction: true,
    silent: true,
    // Store URL so notificationclick handler navigates to /log/sleep to end the nap
    data: { url: '/log/sleep' },
  } as NotificationOptions)
}

export async function endNapLiveActivity(): Promise<void> {
  // iOS native path
  const ios = iosPlugin()
  if (ios) {
    try {
      const activityId = localStorage.getItem(STORAGE_KEY)
      await ios.endNap({ activityId })
      localStorage.removeItem(STORAGE_KEY)
      return
    } catch {
      // fall through to web fallback dismiss
    }
  }

  // Web fallback: dismiss the persistent notification
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (!('serviceWorker' in navigator)) return
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return
  const notifs = await reg.getNotifications({ tag: NAP_TAG })
  for (const n of notifs) n.close()
}
